/**
=========================================================
* Material Dashboard 2 React - v2.1.0
=========================================================

* Product Page: https://www.creative-tim.com/product/material-dashboard-react
* Copyright 2022 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

// @mui material components
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";

// Material Dashboard 2 React components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";

// Material Dashboard 2 React example components
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import DataTable from "examples/Tables/DataTable";
import MDButton from "components/MDButton";
import IconButton from '@mui/material/IconButton';
import Icon from "@mui/material/Icon";
import ConfirmDialog from "components/ConfirmDialog";
import TableSkeleton from "components/TableSkeleton";
import CustomAlert from "components/CustomAlert";

// Data
import { useEffect, useState, useMemo } from "react";
import { RoleAPI } from "api/RoleAPI";
import { useLocation, useNavigate, Link } from "react-router-dom";
import { useMaterialUIController, setLoading } from "context";
import permissions from "permissions";


function ListRole() {
  const [controller, dispatch] = useMaterialUIController();
  const navigate = useNavigate();
  const location = useLocation();
  const message = location.state;
  const [roles, setRoles] = useState([]);
  const [alert, setAlert] = useState(false);
  const [alertContent, setAlertContent] = useState('');
  const [alertColor, setAlertColor] = useState('info');
  const [id, setId] = useState('');
  const [isDeleting, setIsDeleting] = useState(false);
  const { user, loading, admin } = controller;

  const handleAlert = (content, color) => {
    setAlertContent(content);
    setAlertColor(color);
    setAlert(true)
  }

  const handleAlertClose = () => setAlert(false);

  const handleClick = () => {
    navigate('create');
  }

  const handleDelete = (id) => {
    setId(id)
    setIsDeleting(true);
  }

  const handleCancelDelete = () => {
    setIsDeleting(false);
  };

  const handleConfirmDelete = () => {
    return deleteRole(id)
  };

  const renderTable = useMemo(() => {
    let rows = [];
    const columns = [
      { Header: "rol", accessor: "role", width: "45%", align: "left" },
      { Header: "acción", accessor: "action", align: "center" },
    ];

    roles.map((element) => {
      rows = [...rows,
      {
        role: element.name,
        action: (
          <MDBox>
            {(user?.permissions?.includes(permissions.roles[2]) || user?.role === admin) && (
              <IconButton component={Link} aria-label="edit" value={element.id} to={element.id + '/edit'}>
                <Icon fontSize="small">edit</Icon>
              </IconButton>
            )}
            {(user.permissions?.includes(permissions.roles[3]) || user?.role === admin) && (
              <IconButton aria-label="delete" value={element.id} onClick={() => handleDelete(element.id)}>
                <Icon fontSize="small">delete</Icon>
              </IconButton>
            )}
          </MDBox>
        ),
      }
      ]
    });


    return <DataTable
      table={{ columns, rows }}
      isSorted={true}
      entriesPerPage={true}
      showTotalEntries={true}
      noEndBorder
      canSearch={true}
    />
  }, [roles])

  const deleteRole = (id) => {
    return RoleAPI.delete(id)
      .then(response => {
        const updatedRoles = roles.filter((role) => role.id !== response.role);
        setRoles(updatedRoles);
        handleAlert(response.message, 'success');
      }).catch(error => {
        if (error.response) {
          handleAlert(error.response.data.message, 'error')
        }
      }).finally(() => {
        handleCancelDelete();
      });
  }

  const getRoles = (signal) => {
    return RoleAPI.index(signal)
      .then(response => {
        setRoles(response.roles)
      }).catch(error => {
        if (error.response)
          handleAlert(error.response.data.message, 'error')
      }).finally(() => {
        setLoading(dispatch, false)
      });
  }

  useEffect(() => {
    if (message) {
      handleAlert(message, 'success');
    }
  }, [message]);

  useEffect(() => {
    window.history.replaceState({}, document.title)
    const abortController = new AbortController();
    const signal = abortController.signal

    setLoading(dispatch, true)
    getRoles(signal)

    return () => {
      abortController.abort();
    };
  }, []);



  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox pb={3}>
        <Grid container spacing={6}>
          <Grid item xs={12} lg={12}>
            <MDBox mb={4} px={2}>
              <CustomAlert severity={alertColor} message={alertContent} open={alert} onClose={handleAlertClose}></CustomAlert>
            </MDBox>
            <MDBox sx={{ display: 'flex', flexDirection: 'row-reverse', pb: 3 }}>
              {(user?.permissions?.includes(permissions.roles[1]) || user?.role === admin) && (
                <MDButton variant="gradient" color="info" onClick={handleClick}> Crear Rol</MDButton>
              )}
            </MDBox>
            <Card>
              {loading ? <TableSkeleton /> :
                <MDBox pt={3}>
                  {renderTable}
                </MDBox>
              }
            </Card>
            {isDeleting && (<ConfirmDialog onConfirm={handleConfirmDelete} onCancel={handleCancelDelete} ></ConfirmDialog>)}
          </Grid>
        </Grid>
      </MDBox>
      <Footer />
    </DashboardLayout>
  );
}

export default ListRole;